/**
 * Error boundary with two variants:
 *  - <ErrorBoundary scope="app">     full-page recovery card
 *  - <ErrorBoundary scope="tool">    inline tool-panel recovery card
 *
 * Catches both render-time exceptions and lazy-chunk load failures
 * (which surface as ChunkLoadError when a dynamic import 404s after deploy).
 */

import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { trackError } from "@/lib/utils/analytics";

type Scope = "app" | "tool";

interface Props {
  children: ReactNode;
  scope?: Scope;
  /** A label used in the user-facing message, e.g. tool name. */
  label?: string;
  /** Optional override for the reset callback. */
  onReset?: () => void;
}

interface State {
  error: Error | null;
  errorCode: string;
}

function shortHash(input: string): string {
  // Deterministic 6-char hash for support traceability (no PII).
  let h = 0;
  for (let i = 0; i < input.length; i++) h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36).slice(0, 6).toUpperCase();
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorCode: "" };

  static getDerivedStateFromError(error: Error): State {
    return { error, errorCode: shortHash(`${error.name}|${error.message}`) };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Surface to console with a clear marker so dev/prod monitoring can pick it up.
    console.error(
      "[ErrorBoundary]",
      this.state.errorCode,
      error,
      info.componentStack
    );
    // Privacy-safe, opt-out/DNT-gated report (no-op unless analytics is enabled).
    const isChunk =
      error.name === "ChunkLoadError" ||
      /Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(error.message);
    trackError(error.name, this.state.errorCode, this.props.scope ?? "app", isChunk);
  }

  private handleReset = () => {
    this.setState({ error: null, errorCode: "" });
    this.props.onReset?.();
  };

  private handleReload = () => {
    location.reload();
  };

  render() {
    const { error, errorCode } = this.state;
    const { children, scope = "app", label } = this.props;

    if (!error) return children;

    const isChunkError =
      error.name === "ChunkLoadError" ||
      /Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(error.message);

    const friendly = isChunkError
      ? "We couldn't load that part of the app. This usually means a new version was deployed while you had the page open. Please refresh."
      : scope === "tool"
        ? `The "${label ?? "tool"}" panel crashed. You can try again or pick another tool.`
        : "Something went wrong. You can try again or refresh the page.";

    return (
      <div
        role="alert"
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: scope === "app" ? 40 : 20,
          gap: 12,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: "hsl(var(--destructive) / 0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "hsl(var(--destructive))",
          }}
        >
          <AlertTriangle style={{ width: 20, height: 20 }} />
        </div>

        <p style={{ fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))" }}>
          {scope === "app" ? "Application error" : "Tool error"}
        </p>

        <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", maxWidth: 360, lineHeight: 1.5 }}>
          {friendly}
        </p>

        {/* Error reference code for support */}
        <code
          style={{
            fontSize: 10,
            fontFamily: "ui-monospace, monospace",
            padding: "3px 8px",
            background: "hsl(var(--muted))",
            color: "hsl(var(--muted-foreground))",
            borderRadius: 4,
            letterSpacing: "0.06em",
          }}
        >
          ref: {errorCode}
        </code>

        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          {!isChunkError && (
            <button
              onClick={this.handleReset}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                background: "hsl(var(--accent))",
                color: "hsl(var(--foreground))",
                border: "1px solid hsl(var(--border))",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          )}
          <button
            onClick={this.handleReload}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              background: "hsl(var(--brand-strong))",
              color: "hsl(var(--brand-fg))",
              border: "none",
              cursor: "pointer",
            }}
          >
            <RefreshCw style={{ width: 11, height: 11 }} />
            Reload
          </button>
        </div>
      </div>
    );
  }
}
